import React, { useState } from 'react';
import { 
  Users, 
  Search, 
  Mail, 
  Phone, 
  Award, 
  GraduationCap,
  UserCheck,
  Shield,
  Filter,
  Eye
} from 'lucide-react';

const StaffManagementWidget = ({ allStaff = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Role configuration
  const roleConfig = {
    hod: {
      label: 'HOD',
      icon: Shield,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    mentoring_coordinator: {
      label: 'Mentoring Coordinator',
      icon: Award,
      color: 'blue',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
    class_coordinator: {
      label: 'Class Coordinator',
      icon: GraduationCap,
      color: 'green',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    },
    mentor: {
      label: 'Mentor',
      icon: UserCheck,
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
      borderColor: 'border-orange-200',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    }
  };

  // Get role statistics
  const roleStats = allStaff.reduce((acc, staff) => {
    const role = staff.role || 'other';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  // Filter staff
  const filteredStaff = allStaff.filter(staff => {
    const matchesRole = roleFilter === 'All' || staff.role === roleFilter;
    const matchesSearch = searchQuery === '' || 
      (staff.name && staff.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (staff.email && staff.email.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesRole && matchesSearch;
  });

  const getRoleConfig = (role) => {
    return roleConfig[role] || {
      label: role || 'Staff',
      icon: Users,
      color: 'gray',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200',
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600'
    };
  };

  const renderStaffCard = (staff, index) => {
    const config = getRoleConfig(staff.role);
    const RoleIcon = config.icon;

    return (
      <div
        key={index}
        className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer"
        onClick={() => setSelectedStaff(staff)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            {/* Avatar/Icon */}
            <div className={`w-12 h-12 rounded-full ${config.iconBg} flex items-center justify-center flex-shrink-0`}>
              <RoleIcon className={`w-6 h-6 ${config.iconColor}`} />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-800 truncate">{staff.name || 'Unknown'}</h4>
              
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${config.bgColor} ${config.textColor}`}>
                  {config.label}
                </span>
                {staff.division && (
                  <span className="text-xs text-gray-500">
                    Division {staff.division}
                  </span>
                )}
                {staff.batch && (
                  <span className="text-xs text-gray-500">
                    Batch {staff.batch}
                  </span>
                )}
              </div>

              <div className="mt-2 space-y-1">
                {staff.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                    <span className="truncate">{staff.email}</span>
                  </div>
                )}
                {staff.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                    <span>{staff.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* View Details Button */}
          <button 
            className="ml-2 p-2 rounded-lg bg-gray-50 text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStaff(staff);
            }}
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedStaff) return null;
    
    const config = getRoleConfig(selectedStaff.role);
    const RoleIcon = config.icon;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={() => setSelectedStaff(null)}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`${config.bgColor} ${config.borderColor} border-b px-6 py-5`}>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full ${config.iconBg} flex items-center justify-center`}>
                <RoleIcon className={`w-8 h-8 ${config.iconColor}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800">{selectedStaff.name || 'Unknown'}</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${config.bgColor} ${config.textColor} mt-1`}>
                  {config.label}
                </span>
              </div>
              <button
                onClick={() => setSelectedStaff(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Contact Information
                </h4>
                <div className="space-y-3">
                  {selectedStaff.email && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <div className="text-sm font-medium text-gray-800">{selectedStaff.email}</div>
                      </div>
                    </div>
                  )}
                  {selectedStaff.phone && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="text-sm font-medium text-gray-800">{selectedStaff.phone}</div>
                      </div>
                    </div>
                  )}
                  {!selectedStaff.email && !selectedStaff.phone && (
                    <div className="text-sm text-gray-400 italic">No contact information available</div>
                  )}
                </div>
              </div>

              {/* Assignment Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Assignment Details
                </h4>
                <div className="space-y-3">
                  {selectedStaff.division && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <GraduationCap className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Division</div>
                        <div className="text-sm font-medium text-gray-800">{selectedStaff.division}</div>
                      </div>
                    </div>
                  )}
                  {selectedStaff.batch && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs text-gray-500">Batch</div>
                        <div className="text-sm font-medium text-gray-800">{selectedStaff.batch}</div>
                      </div>
                    </div>
                  )}
                  {!selectedStaff.division && !selectedStaff.batch && (
                    <div className="text-sm text-gray-400 italic">No assignment details</div>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            {(selectedStaff.department || selectedStaff.designation) && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Additional Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedStaff.department && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500">Department</div>
                      <div className="text-sm font-medium text-gray-800">{selectedStaff.department}</div>
                    </div>
                  )}
                  {selectedStaff.designation && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500">Designation</div>
                      <div className="text-sm font-medium text-gray-800">{selectedStaff.designation}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl">
            <button
              onClick={() => setSelectedStaff(null)}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 w-full">
      <div className="flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Staff Directory
              </h3>
              <p className="text-sm text-gray-500">{allStaff.length} staff members</p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(roleConfig).map(([roleKey, config]) => {
            const count = roleStats[roleKey] || 0;
            const Icon = config.icon;
            return (
              <div
                key={roleKey}
                className={`p-4 rounded-xl border ${config.borderColor} ${config.bgColor} transition-all hover:shadow-md`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.iconBg}`}>
                    <Icon className={`w-5 h-5 ${config.iconColor}`} />
                  </div>
                  <div>
                    <div className={`text-2xl font-bold ${config.textColor}`}>{count}</div>
                    <div className="text-xs text-gray-600">{config.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-sm"
            />
          </div>
          <div className="relative">
            <Filter className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full sm:w-48 pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-sm appearance-none bg-white"
            >
              <option value="All">All Roles</option>
              <option value="hod">HOD</option>
              <option value="mentoring_coordinator">Mentoring Coordinator</option>
              <option value="class_coordinator">Class Coordinator</option>
              <option value="mentor">Mentor</option>
            </select>
          </div>
        </div>

        {/* Staff List */}
        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
          {filteredStaff.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No staff members found</p>
            </div>
          ) : (
            filteredStaff.map((staff, index) => renderStaffCard(staff, index))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-200">
          <div>
            Showing <span className="font-semibold text-gray-700">{filteredStaff.length}</span> of{' '}
            <span className="font-semibold text-gray-700">{allStaff.length}</span> staff members
          </div>
          <div className="text-gray-400">
            Click on any card to view details
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {renderDetailModal()}
    </div>
  );
};

export default StaffManagementWidget;